import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Download, ImageOff, LoaderCircle, Lock, X } from "lucide-react";
import {
  createGalleryZipJob,
  downloadBackendFile,
  fetchImageBlobUrl,
  getGalleryAlbums,
  getGalleryPhotos,
  getGalleryZipJob,
  isBackendApiUrl,
  verifyGalleryPin,
} from "@/lib/galleryApi";

const PAGE_SIZE = 60;

export default function ClientGallery() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get("folder") || "";
  const matchBucket = searchParams.get("bucket") || "";
  const [pin, setPin] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem(`gallery:${slug}`) || "");
  const [eventData, setEventData] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`gallery-event:${slug}`) || "null"); } catch { return null; }
  });
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(token));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [imageAspects, setImageAspects] = useState({});
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [downloadSelection, setDownloadSelection] = useState([]);
  const [eventHeroFallbackUrl, setEventHeroFallbackUrl] = useState("");
  const [shareToken] = useState(() => sessionStorage.getItem(`gallery-share-token:${slug}`) || "");
  const sentinelRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingPageRef = useRef(false);

  const selectedIndex = useMemo(() => photos.findIndex((photo) => photo.id === selectedId), [photos, selectedId]);
  const selected = selectedIndex >= 0 ? photos[selectedIndex] : null;
  const visibleAlbums = useMemo(() => albums.filter((album) => Number(album.photoCount || 0) > 0), [albums]);
  const activeAlbum = visibleAlbums.find((album) => album.id === folderId);
  const faceSearch = eventData?.faceSearch;
  const ownPhotosMode = faceSearch?.mode === "own_photos";
  const activeBucket = ownPhotosMode ? (matchBucket === "possible" ? "possible" : "strong") : "";
  const albumTabs = useMemo(() => [
    ...(ownPhotosMode ? [
      { id: "strong", label: "STRONG MATCHES", count: faceSearch?.strongCount || 0, bucket: "strong" },
      { id: "possible", label: "POSSIBLE MATCHES", count: faceSearch?.possibleCount || 0, bucket: "possible" },
    ] : [
      { id: "", label: "ALL", count: eventData?.photoCount || null },
    ]),
    ...visibleAlbums.map((album) => ({
      id: album.id,
      label: formatAlbumLabel(album.folderPath || album.folderName),
      count: album.photoCount,
    })),
  ], [eventData?.photoCount, faceSearch?.possibleCount, faceSearch?.strongCount, ownPhotosMode, visibleAlbums]);
  const photoRows = useMemo(() => buildJustifiedRows(photos, imageAspects), [photos, imageAspects]);
  const firstPhotoCover = useMemo(() => {
    const cover = photos.find((photo) => photo.heroPreviewUrl || photo.highQualityPreviewUrl || photo.previewUrl || photo.thumbnailUrl);
    return cover?.heroPreviewUrl || cover?.highQualityPreviewUrl || cover?.previewUrl || cover?.thumbnailUrl || "";
  }, [photos]);
  const firstAlbumCover = useMemo(() => {
    const album = visibleAlbums.find((item) => item.coverHeroUrl || item.coverUrl);
    return album?.coverHeroUrl || album?.coverUrl || "";
  }, [visibleAlbums]);
  const eventTitle = eventData?.eventName || "Private Gallery";
  const albumTitle = activeAlbum ? formatAlbumTitle(activeAlbum.folderPath || activeAlbum.folderName) : "All Photos";
  const heroCoverUrl = eventData?.coverImageUrl || eventHeroFallbackUrl || firstAlbumCover || (!folderId ? firstPhotoCover : "");
  const loadedCountLabel = photos.length
    ? `${photos.length}${totalPhotos ? ` / ${totalPhotos}` : hasMore ? "+" : ""} photos loaded`
    : "Private archive";

  const loadPage = useCallback(async (offset, append = false) => {
    if (!token || loadingPageRef.current) return;
    loadingPageRef.current = true;
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const result = await getGalleryPhotos(slug, token, PAGE_SIZE, offset, folderId, activeBucket);
      const returnedCount = result.photos?.length || 0;
      const nextOffset = Number(result.pagination?.nextOffset ?? (offset + returnedCount));
      const total = Number(result.pagination?.total || 0);
      const nextHasMore = returnedCount > 0
        && Boolean(result.pagination?.hasMore ?? (total ? nextOffset < total : returnedCount >= PAGE_SIZE));
      setEventData((current) => {
        const next = {
          ...(current || {}),
          ...(result.event || {}),
          photoCount: folderId ? current?.photoCount : total || current?.photoCount,
          ...(result.faceSearch ? { faceSearch: result.faceSearch } : {}),
        };
        sessionStorage.setItem(`gallery-event:${slug}`, JSON.stringify(next));
        return next;
      });
      setPhotos((current) => append ? [...current, ...(result.photos || [])] : (result.photos || []));
      setTotalPhotos(total);
      setHasMore(nextHasMore);
      offsetRef.current = nextOffset;
      logPagination({
        selectedFolderId: activeBucket || folderId || "ALL",
        loadedCount: append ? offset + returnedCount : returnedCount,
        totalCount: total,
        offset,
        nextOffset,
        hasMore: nextHasMore,
        isLoadingMore: append,
        batchSize: PAGE_SIZE,
        returnedCount,
      });
    } catch (err) {
      if (!append) {
        sessionStorage.removeItem(`gallery:${slug}`);
        setToken("");
      }
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingPageRef.current = false;
    }
  }, [activeBucket, folderId, slug, token]);

  useEffect(() => {
    if (!token) return;
    getGalleryAlbums(slug, token)
      .then((result) => setAlbums(result.albums || []))
      .catch(() => setAlbums([]));
  }, [slug, token]);

  useEffect(() => {
    if (!token || eventData?.coverImageUrl || eventHeroFallbackUrl) return;
    let cancelled = false;
    getGalleryPhotos(slug, token, 1, 0, "")
      .then((result) => {
        if (cancelled) return;
        const cover = result.photos?.find((photo) => photo.heroPreviewUrl || photo.highQualityPreviewUrl || photo.previewUrl || photo.thumbnailUrl);
        const nextUrl = cover?.heroPreviewUrl || cover?.highQualityPreviewUrl || cover?.previewUrl || cover?.thumbnailUrl || "";
        if (nextUrl) setEventHeroFallbackUrl(nextUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eventData?.coverImageUrl, eventHeroFallbackUrl, slug, token]);

  useEffect(() => {
    if (!token) return;
    setPhotos([]);
    setLoadedImages({});
    setFailedImages({});
    setImageAspects({});
    setSelectedId(null);
    setPreviewLoaded(false);
    setPreviewError("");
    setDownloadSelection([]);
    setTotalPhotos(0);
    offsetRef.current = 0;
    loadingPageRef.current = false;
    loadPage(0);
  }, [token, folderId, activeBucket, loadPage]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || loadingMore || loadingPageRef.current) return;
    loadPage(offsetRef.current, true);
  }, [hasMore, loadingMore, loadPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadNextPage();
    }, { rootMargin: "1200px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadNextPage, photos.length, folderId]);

  useEffect(() => {
    if (!hasMore) return undefined;
    const onScroll = () => {
      const remaining = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      if (remaining < 1200) loadNextPage();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, loadNextPage]);

  useEffect(() => {
    if (!selected) return undefined;
    setPreviewLoaded(false);
    setPreviewError("");
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedId(null);
      if (event.key === "ArrowLeft" && selectedIndex > 0) setSelectedId(photos[selectedIndex - 1].id);
      if (event.key === "ArrowRight" && selectedIndex < photos.length - 1) setSelectedId(photos[selectedIndex + 1].id);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected, selectedIndex, photos]);

  const unlock = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await verifyGalleryPin(slug, pin);
      sessionStorage.setItem(`gallery:${slug}`, result.token);
      sessionStorage.setItem(`gallery-event:${slug}`, JSON.stringify(result.event || {}));
      setEventData(result.event);
      setToken(result.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectAlbum = (nextFolderId) => {
    if (nextFolderId) setSearchParams({ folder: nextFolderId });
    else setSearchParams({});
  };
  const selectMatchBucket = (bucket) => setSearchParams(bucket === "possible" ? { bucket: "possible" } : { bucket: "strong" });

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070707] text-[#F7F1E8] flex items-center justify-center px-6 pt-24">
        <form onSubmit={unlock} className="w-full max-w-md border border-[#C99A45]/25 p-8 md:p-12 text-center bg-black/30">
          <Lock className="text-[#C99A45] mx-auto" size={28} />
          <div className="eyebrow text-[#C99A45] mt-6">Private Collection</div>
          <h1 className="font-display text-4xl mt-3">Enter Gallery PIN</h1>
          <input value={pin} onChange={(e) => setPin(e.target.value)} type="password" inputMode="numeric" autoFocus
            className="w-full bg-transparent border border-[#C99A45]/35 px-4 py-3 mt-8 text-center tracking-[0.35em] outline-none focus:border-[#C99A45]" placeholder="PIN" />
          {error && <p className="text-[#E52717] text-sm mt-4">{error}</p>}
          <button disabled={loading} className="btn-gold-fill w-full mt-6">{loading ? "Checking..." : "View Gallery"}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#F7F1E8] pb-16">
      <section className="client-gallery-hero">
        {heroCoverUrl ? (
          <GalleryImage
            src={heroCoverUrl}
            alt=""
            priority
            mode="hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 gallery-image-skeleton" />
        )}
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-y-0 left-0 w-[34%] max-w-[640px] bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[32%] bg-gradient-to-t from-black/65 via-black/18 to-transparent" />
        <div className="relative z-[1] h-full max-w-[1680px] mx-auto px-5 md:px-10 flex items-end pb-14 md:pb-20">
          <div className="max-w-4xl">
            <div className="eyebrow text-[10px] text-[#C99A45] mb-4">Private Client Gallery</div>
            <h1 className="font-display text-[clamp(2.2rem,5vw,4.9rem)] leading-[0.95] text-white drop-shadow-[0_12px_32px_rgba(0,0,0,.55)]">
              {eventTitle}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-white/65">
              <span>{loadedCountLabel}</span>
              <span>{eventTitle}</span>
              <span>Mangalfera Films</span>
            </div>
            <button
              type="button"
              onClick={() => document.getElementById("client-gallery-photos")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-gold-fill mt-8"
            >
              View Gallery
            </button>
          </div>
        </div>
      </section>

      <div className="client-gallery-tabs">
        <div className="max-w-[1680px] mx-auto px-3 md:px-8 flex items-center gap-2 overflow-x-auto">
          {albumTabs.map((tab) => (
            <button
              key={tab.id || "all"}
              type="button"
              onClick={() => tab.bucket ? selectMatchBucket(tab.bucket) : selectAlbum(tab.id)}
              className={`client-gallery-tab ${(tab.bucket ? tab.bucket === activeBucket : tab.id === folderId) ? "is-active" : ""}`}
            >
              <span>{tab.label}</span>
              {Number(tab.count || 0) > 0 && <span className="text-[10px] opacity-50">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <main id="client-gallery-photos" className="max-w-[1680px] mx-auto px-1.5 md:px-4 pt-4 md:pt-6">
        <div className="px-3 md:px-4 pb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="eyebrow text-[9px] text-[#C99A45]">{ownPhotosMode ? "My Photos" : folderId ? "Album" : "Collection"}</div>
            <h2 className="font-display text-3xl md:text-4xl mt-1">
              {ownPhotosMode ? (activeBucket === "possible" ? "Possible Matches" : "Strong Matches") : folderId ? `Album: ${albumTitle}` : "All Photos"}
            </h2>
            {ownPhotosMode && (
              <p className="text-sm text-[#F7F1E8]/55 mt-2 max-w-2xl">
                {activeBucket === "possible"
                  ? "These photos may include you. Please review."
                  : "Photos we confidently matched from your selfie."}
              </p>
            )}
            {ownPhotosMode && faceSearch?.message && (
              <p className="text-xs text-[#C99A45]/80 mt-2 max-w-2xl">{faceSearch.message}</p>
            )}
            {ownPhotosMode && shareToken && (
              <button
                type="button"
                onClick={() => navigate(`/gallery/share/${shareToken}`)}
                className="btn-gold-outline mt-4"
              >
                Retake selfie / upload another selfie
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {folderId && (
              <button onClick={() => selectAlbum("")} className="btn-gold-outline flex items-center gap-2">
                <ArrowLeft size={14} /> All Photos
              </button>
            )}
            {eventData?.downloadEnabled && (
              <>
                {downloadSelection.length > 0 && (
                  <PrepareZipButton slug={slug} token={token} folderId={folderId} photoIds={downloadSelection}
                    bucket={activeBucket} label={`Download selected (${downloadSelection.length})`} outline />
                )}
                <PrepareZipButton slug={slug} token={token} folderId={folderId} bucket={activeBucket} label={ownPhotosMode ? `Download ${activeBucket}` : folderId ? "Download album" : "Download all"} />
              </>
            )}
          </div>
        </div>

        {error && <div className="border border-red-500/30 text-red-300 p-4 mb-6 mx-3">{error}</div>}
        {loading ? (
          <GallerySkeleton />
        ) : photos.length ? (
          <>
            <div className="client-gallery-wall">
              {photoRows.map((row) => (
                <div key={row.id} className="client-gallery-row" style={{ "--row-height": row.height }}>
                  {row.photos.map((photo) => (
                    <GalleryTile
                      key={photo.id}
                      photo={photo}
                      aspect={getPhotoAspect(photo, imageAspects)}
                      loaded={Boolean(loadedImages[photo.id])}
                      failed={Boolean(failedImages[photo.id])}
                      selectable={Boolean(eventData?.downloadEnabled)}
                      selected={downloadSelection.includes(photo.id)}
                      onDimensions={(w, h) => {
                        // Update aspect from backend header immediately so portrait
                        // photos display at correct height without reload.
                        setImageAspects((current) => ({
                          ...current,
                          [photo.id]: w / h,
                        }));
                      }}
                      onLoad={(event) => {
                        const image = event.currentTarget;
                        if (image.naturalWidth && image.naturalHeight) {
                          setImageAspects((current) => ({
                            ...current,
                            [photo.id]: image.naturalWidth / image.naturalHeight,
                          }));
                        }
                        setLoadedImages((current) => ({ ...current, [photo.id]: true }));
                      }}
                      onError={() => setFailedImages((current) => ({ ...current, [photo.id]: true }))}
                      onClick={() => (photo.highQualityPreviewUrl || photo.previewUrl) && setSelectedId(photo.id)}
                      onSelect={() => setDownloadSelection((current) =>
                        current.includes(photo.id) ? current.filter((id) => id !== photo.id) : [...current, photo.id]
                      )}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div ref={sentinelRef} className="h-24 flex items-center justify-center">
              {loadingMore && <LoaderCircle size={24} className="text-[#C99A45] animate-spin" />}
              {!loadingMore && hasMore && (
                <button type="button" onClick={loadNextPage} className="btn-gold-outline">
                  Load more photos
                </button>
              )}
              {!hasMore && photos.length > 0 && (
                <div className="eyebrow text-[9px] text-[#F7F1E8]/35">
                  End of collection
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center border border-[#C99A45]/15 p-12 text-[#F7F1E8]/55 mx-3">
            {ownPhotosMode
              ? activeBucket === "possible"
                ? "No possible matches found yet. Retake a clearer selfie or contact Mangalfera Films."
                : "No confident matches found. Check Possible Matches, retake a clearer selfie, or contact Mangalfera Films."
              : "No photographs have been synced yet."}
            {ownPhotosMode && shareToken && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => navigate(`/gallery/share/${shareToken}`)}
                  className="btn-gold-fill"
                >
                  Retake selfie
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {selected && (
        <Lightbox
          photo={selected}
          index={selectedIndex}
          total={photos.length}
          previewLoaded={previewLoaded}
          previewError={previewError}
          onClose={() => setSelectedId(null)}
          onPrevious={selectedIndex > 0 ? () => setSelectedId(photos[selectedIndex - 1].id) : null}
          onNext={selectedIndex < photos.length - 1 ? () => setSelectedId(photos[selectedIndex + 1].id) : null}
          onLoaded={(event) => {
            setPreviewLoaded(true);
            if (process.env.NODE_ENV !== "production") {
              console.info("[GALLERY_PREVIEW_QUALITY]", {
                photoId: selected.id,
                filename: selected.name,
                naturalWidth: event.currentTarget.naturalWidth,
                naturalHeight: event.currentTarget.naturalHeight,
              });
            }
          }}
          onError={() => {
            console.warn("[GALLERY_HIGH_QUALITY_PREVIEW_FAILED]", {
              photoId: selected.id,
              filename: selected.name,
            });
            setPreviewError("High quality preview failed to load");
          }}
          onDownload={() => downloadBackendFile(selected.downloadUrl, selected.name).catch((err) => setError(err.message))}
        />
      )}
    </div>
  );
}

function Lightbox({ photo, index, total, previewLoaded, previewError, onClose, onPrevious, onNext, onLoaded, onError, onDownload }) {
  const previewSrc = photo.highQualityPreviewUrl || photo.previewUrl;
  return (
    <div className="fixed inset-0 z-[100] bg-black/96 flex items-center justify-center p-3 md:p-5" onClick={onClose}>
      <button className="absolute top-5 right-5 text-white z-20 p-2 bg-black/35 border border-white/10" onClick={onClose} aria-label="Close preview"><X /></button>
      {onPrevious && (
        <button className="absolute left-3 md:left-8 text-white/80 z-20 p-3 bg-black/25 border border-white/10" onClick={(event) => {
          event.stopPropagation(); onPrevious();
        }} aria-label="Previous photo"><ChevronLeft size={36} /></button>
      )}
      {onNext && (
        <button className="absolute right-3 md:right-8 text-white/80 z-20 p-3 bg-black/25 border border-white/10" onClick={(event) => {
          event.stopPropagation(); onNext();
        }} aria-label="Next photo"><ChevronRight size={36} /></button>
      )}

      {!previewLoaded && !previewError && photo.thumbnailUrl && (
        <GalleryImage
          src={photo.thumbnailUrl}
          alt=""
          decoding="async"
          photoId={photo.id}
          filename={photo.name}
          className="absolute max-h-[78vh] max-w-[88vw] object-contain opacity-30 blur-sm"
          onClick={(event) => event.stopPropagation()}
        />
      )}

      {!previewLoaded && !previewError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-3 text-[#C99A45] eyebrow text-[10px] tracking-[0.28em] bg-black/50 border border-[#C99A45]/20 px-4 py-3">
            <LoaderCircle size={18} className="animate-spin" /> Loading high quality preview
          </div>
        </div>
      )}

      {previewError && (
        <div className="absolute inset-0 flex items-center justify-center px-6 pointer-events-none">
          <div className="border border-red-500/30 bg-black/75 px-5 py-4 text-center text-sm text-red-200">
            High quality preview unavailable
          </div>
        </div>
      )}

      {!previewError && previewSrc && (
        <GalleryImage
          src={previewSrc}
          alt={photo.name}
          decoding="async"
          priority
          mode="preview"
          photoId={photo.id}
          filename={photo.name}
          width={photo.width || undefined}
          height={photo.height || undefined}
          onLoad={onLoaded}
          onError={onError}
          className={`client-gallery-lightbox-image transition-opacity duration-300 ${previewLoaded ? "opacity-100" : "opacity-0"}`}
          onClick={(event) => event.stopPropagation()}
        />
      )}

      <div className="absolute bottom-4 md:bottom-5 left-4 right-4 flex items-center justify-between gap-4 text-xs text-white/68">
        <span className="truncate">{photo.name} - {index + 1} / {total}</span>
        {photo.downloadUrl && (
          <button type="button" className="btn-gold-fill flex items-center gap-2 shrink-0"
            onClick={(event) => {
              event.stopPropagation();
              onDownload();
            }}><Download size={15} /> Download</button>
        )}
      </div>
    </div>
  );
}

function GalleryTile({ photo, aspect, loaded, failed, selectable, selected, onLoad, onError, onClick, onSelect, onDimensions }) {
  const aspectRatio = clampAspect(aspect || 4 / 3);
  if (!photo.thumbnailUrl || failed) {
    return (
      <div className="client-gallery-tile bg-[#111] border border-white/5 flex items-center justify-center text-white/25"
        style={{ "--tile-aspect": aspectRatio, "--tile-flex": aspectRatio }}>
        <ImageOff size={20} />
      </div>
    );
  }
  return (
    <div className="client-gallery-tile group" style={{ "--tile-aspect": aspectRatio, "--tile-flex": aspectRatio }}>
      <button onClick={onClick} className="absolute inset-0 w-full h-full" aria-label={`Open ${photo.name}`}>
        <div className={`absolute inset-0 gallery-image-skeleton transition-opacity duration-300 ${loaded ? "opacity-0" : "opacity-100"}`} />
        <GalleryImage src={photo.thumbnailUrl} alt={photo.name} loading="lazy" decoding="async"
          mode="grid"
          photoId={photo.id} filename={photo.name}
          width={photo.width || undefined} height={photo.height || undefined}
          onLoad={onLoad} onError={onError} onDimensions={onDimensions}
          className={`w-full h-full object-cover transition duration-500 ${loaded ? "opacity-100 group-hover:scale-[1.025]" : "opacity-0"}`} />
      </button>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent opacity-0 group-hover:opacity-100 transition" />
      {selectable && (
        <button type="button" onClick={(event) => { event.stopPropagation(); onSelect(); }}
          className={`absolute top-2 left-2 z-10 w-7 h-7 border flex items-center justify-center text-xs ${
            selected ? "bg-[#C99A45] border-[#C99A45] text-black" : "bg-black/55 border-white/45 text-white"
          }`} aria-label={`${selected ? "Deselect" : "Select"} ${photo.name}`}>
          {selected ? "✓" : ""}
        </button>
      )}
    </div>
  );
}

function getPhotoAspect(photo, imageAspects = {}) {
  const measured = imageAspects[photo.id];
  if (measured) return measured;
  if (photo.width && photo.height) return photo.width / photo.height;
  return 4 / 3;
}

function clampAspect(aspect) {
  return Math.min(Math.max(Number(aspect) || 4 / 3, 0.5), 2.25);
}

function buildJustifiedRows(photos, imageAspects = {}) {
  const rows = [];
  let row = [];
  let ratioSum = 0;
  const targetRatio = 4.25;

  photos.forEach((photo) => {
    const aspect = clampAspect(getPhotoAspect(photo, imageAspects));
    row.push(photo);
    ratioSum += aspect;
    if (ratioSum >= targetRatio || row.length >= 5) {
      rows.push(makeRow(row, ratioSum));
      row = [];
      ratioSum = 0;
    }
  });

  if (row.length) rows.push(makeRow(row, ratioSum, true));
  return rows;
}

function makeRow(photos, ratioSum, isLast = false) {
  const height = isLast && ratioSum < 2.2
    ? "clamp(230px, 24vw, 330px)"
    : "clamp(240px, 22vw, 350px)";
  return {
    id: photos.map((photo) => photo.id).join("-"),
    photos,
    height,
  };
}

function GalleryImage({ src, onError, onDimensions, photoId, filename, className = "", style, priority = false, mode = "image", ...props }) {
  const [resolvedSrc, setResolvedSrc] = useState("");
  const onErrorRef = useRef(onError);
  const onDimensionsRef = useRef(onDimensions);

  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onDimensionsRef.current = onDimensions; }, [onDimensions]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    setResolvedSrc("");
    if (!src) return undefined;

    fetchImageBlobUrl(src, { priority })
      .then(({ objectUrl: blobUrl, naturalWidth, naturalHeight }) => {
        if (!active) {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          return;
        }
        objectUrl = blobUrl;
        setResolvedSrc(blobUrl);
        // Surface backend-corrected dimensions immediately (before onLoad fires)
        // so the grid can fix aspect ratio without waiting for the next API call.
        if (naturalWidth && naturalHeight && onDimensionsRef.current) {
          onDimensionsRef.current(naturalWidth, naturalHeight);
        }
      })
      .catch((error) => {
        console.error("[IMAGE_LOAD_ERROR]", {
          photoId,
          filename,
          url: isBackendApiUrl(src) ? src.replace(/token=[^&]+/i, "token=REDACTED") : src,
          message: error.message,
        });
        if (active) onErrorRef.current?.();
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename, photoId, priority, src]);

  if (!resolvedSrc) {
    return <span aria-hidden="true" className={className} style={style} />;
  }

  const { onLoad, ...imageProps } = props;
  return <img
    src={resolvedSrc}
    onError={onError}
    onLoad={(event) => {
      logImageSize(mode, src, photoId, filename, event.currentTarget);
      onLoad?.(event);
    }}
    className={className}
    style={style}
    {...imageProps}
  />;
}

function logImageSize(mode, src, photoId, filename, image) {
  const debugEnabled = mode === "preview"
    || mode === "hero"
    || (typeof window !== "undefined" && window.localStorage?.getItem("galleryDebugImages") === "1");
  if (!debugEnabled) return;
  console.info("[GALLERY_IMAGE_SIZE]", {
    mode,
    photoId,
    filename,
    naturalWidth: image.naturalWidth,
    naturalHeight: image.naturalHeight,
    displayedWidth: Math.round(image.getBoundingClientRect().width),
    displayedHeight: Math.round(image.getBoundingClientRect().height),
    route: imageRouteKind(src),
  });
}

function imageRouteKind(src = "") {
  if (src.includes("/thumbnail")) return "thumbnail";
  if (src.includes("/preview")) return "preview";
  if (src.includes("/download")) return "download";
  if (src.includes("/storage/")) return "storage";
  return "external";
}

function logPagination(details) {
  if (
    typeof window !== "undefined"
    && window.localStorage?.getItem("galleryDebugPagination") === "0"
  ) {
    return;
  }
  console.info("[GALLERY_PAGINATION]", details);
}

function GallerySkeleton() {
  return (
    <div className="client-gallery-wall">
      {Array.from({ length: 4 }, (_, rowIndex) => (
        <div key={rowIndex} className="client-gallery-row" style={{ "--row-height": "clamp(240px, 22vw, 350px)" }}>
          {Array.from({ length: rowIndex % 2 ? 4 : 3 }, (_, index) => {
            const aspect = index % 3 === 0 ? 0.68 : index % 2 === 0 ? 1.55 : 1.05;
            return <div key={index} className="client-gallery-tile gallery-image-skeleton" style={{ "--tile-aspect": aspect, "--tile-flex": aspect }} />;
          })}
        </div>
      ))}
    </div>
  );
}

function PrepareZipButton({ slug, token, folderId = "", photoIds = [], bucket = "", label, outline = false, className = "" }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!job?.id || !["queued", "processing"].includes(job.status)) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const result = await getGalleryZipJob(slug, token, job.id);
        setJob(result.job);
      } catch (err) {
        setError(err.message);
      }
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [job, slug, token]);

  const prepare = async () => {
    setError("");
    try {
      const result = await createGalleryZipJob(slug, token, folderId, photoIds, bucket);
      setJob(result.job);
    } catch (err) {
      setError(err.message);
    }
  };

  if (job?.status === "ready" && job.downloadUrl) {
    return <button type="button" onClick={() => downloadBackendFile(job.downloadUrl, `${slug}.zip`).catch((err) => setError(err.message))}
      className={`${outline ? "btn-gold-outline" : "btn-gold-fill"} ${className} items-center gap-2`}>
      <Download size={14} /> Download ZIP
    </button>;
  }

  return (
    <div className={`relative ${className}`}>
      <button type="button" onClick={prepare} disabled={job && ["queued", "processing"].includes(job.status)}
        className={`${outline ? "btn-gold-outline" : "btn-gold-fill"} flex items-center gap-2`}>
        {job && ["queued", "processing"].includes(job.status) ? <LoaderCircle size={14} className="animate-spin" /> : <Download size={14} />}
        {job && ["queued", "processing"].includes(job.status) ? `Preparing ${job.photoCount || ""}...` : label}
      </button>
      {(error || job?.status === "failed" || job?.status === "expired") && (
        <div className="absolute right-0 top-full mt-2 w-64 text-xs text-red-300 bg-black border border-red-500/25 p-2 z-20">
          {error || job?.error || "ZIP expired. Prepare it again."}
        </div>
      )}
    </div>
  );
}

function formatAlbumLabel(value = "") {
  return String(value || "Album").split("/").filter(Boolean).pop().replace(/[-_]+/g, " ").trim().toUpperCase();
}

function formatAlbumTitle(value = "") {
  const label = String(value || "Album").split("/").filter(Boolean).pop().replace(/[-_]+/g, " ").trim();
  return label.replace(/\b\w/g, (letter) => letter.toUpperCase());
}
